// `||`, not `??`: a manifest can render INTERNAL_BACKEND_API_URL as an empty or
// whitespace-only string (empty configMapKeyRef, unsubstituted template value — the
// classic first-deploy mistake for a new env var), and `??` would pass that through
// and silently shadow BACKEND_API_URL. Every caller reads the result as "not
// configured", which in middleware means redirecting every authenticated request to
// sign-in with no 5xx and no Sentry event. Blank means absent here. `.trim()` also
// drops a trailing newline that would otherwise land in the middle of a request URL.
export const getServerBackendApiUrl = (): string | undefined => getServerBackendApiUrls()[0]

/**
 * The auth backends to try, in order: internal first, public as the fallback.
 *
 * INTERNAL_BACKEND_API_URL is a single point of failure for every server-side auth
 * check — middleware runs on all non-`/api` routes and `requireUser` guards the
 * privileged API routes. If it is set and its endpoint dies, users see a sign-in
 * redirect rather than an error, with nothing pointing at the cause. Falling back is
 * not a new posture: BACKEND_API_URL is exactly what these call sites used before
 * the internal route was introduced.
 */
export const getServerBackendApiUrls = (): string[] => {
  const candidates = [
    process.env.INTERNAL_BACKEND_API_URL?.trim(),
    process.env.BACKEND_API_URL?.trim(),
  ].filter((url): url is string => !!url)
  // Deduped without Set spread — the repo targets a pre-ES2015 lib for this path.
  return candidates.filter((url, i) => candidates.indexOf(url) === i)
}

// Middleware runs before every protected page render, so an unbounded fetch does
// not degrade auth — it stalls the whole page until the platform's own function
// timeout fires. Bounded here instead.
export const AUTH_FETCH_TIMEOUT_MS = 5_000

// '' in development, '/api/alpha' in production — the gateway prefix both auth
// call sites have to agree on.
const alphaPath = (): string => (process.env.NODE_ENV === 'development' ? '' : '/api/alpha')

/**
 * GET /users/me against the first backend that answers.
 *
 * Only TRANSPORT failures (network error, timeout) advance to the next candidate —
 * an HTTP response is authoritative, including 401, so a rejected session is never
 * re-asked of a different host. Returns null when no candidate is configured or all
 * of them failed to respond; every caller treats that as unauthenticated.
 */
export async function fetchCurrentUser(headers: Record<string, string>): Promise<Response | null> {
  for (const base of getServerBackendApiUrls()) {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), AUTH_FETCH_TIMEOUT_MS)
    try {
      return await fetch(`${base}${alphaPath()}/users/me`, {
        method: 'GET',
        headers,
        signal: controller.signal,
      })
    } catch {
      // Transport error or timeout — try the next backend.
    } finally {
      clearTimeout(timer)
    }
  }
  return null
}
